import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Modal,
  PermissionGate,
  Checkbox,
} from "components/ui";
import { FileUploader } from "components/ui/FileUploader";
import { useToast } from "hooks/useToast";
import { usePermission } from "hooks/usePermissions";
import { PageId, ActionCode, CompanyStatus } from "types/index";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import {
  AddOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutline,
  UploadFileOutlined,
} from "@mui/icons-material";
import { companiesService } from "services/companies.service";
import { Company } from "types/company";

export const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  //   const canView = usePermission(PageId.COMPANIES, ActionCode.VIEW);
  //   const canAdd = usePermission(PageId.COMPANIES, ActionCode.ADD);
  //   const canEdit = usePermission(PageId.COMPANIES, ActionCode.EDIT);
  //   const canDelete = usePermission(PageId.COMPANIES, ActionCode.DELETE);
  const canView = true;
  const canAdd = true;
  const canEdit = true;
  const canDelete = true;
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await companiesService.list(page, pageSize);
        setRows(res.data);
        setTotal(res.pagination.total);
      } catch (e: any) {
        setError(e?.message || "Failed to load companies");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onAdd = () => {
    navigate(ROUTES.COMPANY_NEW);
  };

  const onView = (id: string) => {
    navigate(`${ROUTES.COMPANIES}/${id}/view`);
  };
  const onEdit = async (id: string) => {
    const name = prompt("Enter new name");
    if (!name) return;
    try {
      await companiesService.update(id, { name });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
    } catch (e: any) {
      alert(e?.message || "Failed to update");
    }
  };
  const onRequestDelete = (id: string) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const onRequestUpdateLicense = (company: Company) => {
    setSelectedCompany(company);
    setSelectedFiles([]);
    setUploadError(null);
    setUpdateOpen(true);
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
    setUploadError(null);
  };

  const onConfirmUpload = async () => {
    if (!selectedCompany) return;
    if (!selectedFiles || selectedFiles.length === 0) {
      setUploadError("Please select a .lic file to upload.");
      return;
    }
    const file = selectedFiles[0];
    setIsUploading(true);
    setUploadError(null);
    try {
      const fileAsBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const result = (reader.result as string) || "";
            const content = result.includes(",")
              ? result.split(",")[1]
              : result;
            resolve(content);
          } catch (e) {
            reject(e);
          }
        };
        reader.onerror = () =>
          reject(reader.error || new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      await companiesService.uploadLicense(
        String(selectedCompany.id),
        file.name,
        fileAsBase64
      );
      toast.success("Subscription updated successfully");

      const res = await companiesService.list(page, pageSize);
      setRows(res.data);
      setTotal(res.pagination.total);

      setUpdateOpen(false);
      setSelectedCompany(null);
      setSelectedFiles([]);
    } catch (e: any) {
      setUploadError(e?.message || "Failed to upload license");
    } finally {
      setIsUploading(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await companiesService.remove(deletingId);
      const res = await companiesService.list(page, pageSize);
      setRows(res.data);
      setTotal(res.pagination.total);
      toast.success("Company deleted successfully");
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete");
    } finally {
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Companies" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {canAdd && (
                <Button variant="primary" size="sm" onClick={onAdd} className="w-full sm:w-auto">
                  <AddOutlined fontSize="small" />
                  <span>Add</span>
                </Button>
              )}
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && pagedRows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {pagedRows.map((comp) => (
              <div
                key={comp.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {comp.name}
                    </h3>
                    {(comp as any).packageName && (
                      <p className="text-sm text-secondary-600 mt-1">
                        Package: {(comp as any).packageName}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {canView && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onView(comp.id)}
                        className="p-2"
                      >
                        <VisibilityOutlined fontSize="small" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`${ROUTES.COMPANIES}/${comp.id}/edit`)
                        }
                        className="p-2"
                      >
                        <EditOutlined fontSize="small" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[120px]">
                      Employees:
                    </span>
                    <span className="text-secondary-900">
                      {typeof comp.nbActiveEmployees === "number"
                        ? comp.nbActiveEmployees + "/" + comp.maxNbEmployees
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[120px]">
                      Total Scans:
                    </span>
                    <span className="text-secondary-900">
                      {comp.totalScans !== undefined ? comp.totalScans : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[120px]">
                      Colors:
                    </span>
                    <div className="flex items-center gap-2">
                      {comp.primaryColor && (
                        <span
                          title={comp.primaryColor}
                          className="inline-block h-5 w-5 rounded-full border border-secondary-200"
                          style={{ backgroundColor: comp.primaryColor }}
                        />
                      )}
                      {comp.secondaryColor && (
                        <span
                          title={comp.secondaryColor}
                          className="inline-block h-5 w-5 rounded-full border border-secondary-200"
                          style={{ backgroundColor: comp.secondaryColor }}
                        />
                      )}
                      {!comp.primaryColor && !comp.secondaryColor && (
                        <span className="text-secondary-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[120px]">
                      Individual:
                    </span>
                    <Checkbox
                      checked={Boolean((comp as any).isFreelancer)}
                      disabled
                    />
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 pt-2 border-t border-secondary-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRequestUpdateLicense(comp)}
                      className="flex-1"
                    >
                      <UploadFileOutlined fontSize="small" />
                      <span>Update Subscription</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Name
                  </th>
                  {/* <th className="text-left p-3 text-secondary-700 font-semibold">
                    Mobile Number
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Address
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Website
                  </th> */}
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Package
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Active Employees
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Total Scans
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Primary Color
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Secondary Color
                  </th>
                  <th className="text-center p-3 text-secondary-700 font-semibold align-middle">
                    Individual
                  </th> 
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && pagedRows.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-secondary-600"
                      colSpan={12}
                    >
                      No data
                    </td>
                  </tr>
                )}
                {pagedRows.map((comp) => (
                  <tr
                    key={comp.id}
                    className="border-t border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="p-3">{comp.name}</td>
                    {/* <td className="p-3">{comp.phoneNumber}</td>
                    <td className="p-3">{comp.address}</td>
                    <td className="p-3">{comp.website}</td>  */}
                    <td className="p-3">{(comp as any).packageName}</td>
                    <td className="p-3">
                      {typeof comp.nbActiveEmployees === "number"
                        ? comp.nbActiveEmployees + "/" + comp.maxNbEmployees
                        : "—"}
                    </td>
                    <td className="p-3">
                      {comp.totalScans !== undefined ? comp.totalScans : "—"}
                    </td>
                    <td className="p-3">
                      {comp.primaryColor ? (
                        <span
                          title={comp.primaryColor}
                          className="inline-block h-5 w-5 rounded-full border border-secondary-200"
                          style={{ backgroundColor: comp.primaryColor }}
                        />
                      ) : (
                        <span className="text-secondary-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {comp.secondaryColor ? (
                        <span
                          title={comp.secondaryColor}
                          className="inline-block h-5 w-5 rounded-full border border-secondary-200"
                          style={{ backgroundColor: comp.secondaryColor }}
                        />
                      ) : (
                        <span className="text-secondary-4 00">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={Boolean((comp as any).isFreelancer)}
                          disabled
                        />
                      </div>
                    </td> 
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {canView && (
                          <IconButton
                            size="small"
                            onClick={() => onView(comp.id)}
                            aria-label="View"
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        )}
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`${ROUTES.COMPANIES}/${comp.id}/edit`)
                            }
                            aria-label="Edit"
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        )}
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() => onRequestUpdateLicense(comp)}
                            aria-label="Update Subscription"
                          >
                            <UploadFileOutlined fontSize="small" />
                          </IconButton>
                        )}
                        {/* {canDelete && (
                          <IconButton
                            size="small"
                            onClick={() => onRequestDelete(comp.id)}
                            aria-label="Delete"
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )} */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={confirmOpen}
        title="Delete Company"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={onConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      >
        Are you sure you want to delete this company?
      </Modal>

      <Modal
        open={updateOpen}
        title={
          selectedCompany
            ? `Update Subscription - ${selectedCompany.name}`
            : "Update Subscription"
        }
        confirmText={isUploading ? "Uploading…" : "Upload"}
        cancelText="Cancel"
        onConfirm={isUploading ? undefined : onConfirmUpload}
        onCancel={() => {
          setUpdateOpen(false);
          setSelectedCompany(null);
          setSelectedFiles([]);
          setUploadError(null);
        }}
      >
        <div className="space-y-3">
          <FileUploader
            label="Upload Subscription"
            accept={{
              "application/octet-stream": [".lic"],
              "text/plain": [".lic"],
            }}
            maxSizeBytes={5 * 1024 * 1024}
            helperText="Max 5MB. Only .lic license file is allowed."
            onFilesSelected={handleFilesSelected}
            onError={(msg) =>
              setUploadError(msg || "Only .lic files are allowed")
            }
          />
          {uploadError && (
            <div className="text-sm text-red-600">{uploadError}</div>
          )}
        </div>
      </Modal>
    </MainLayout>
  );
};
